'use client'

import { Loader2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import {
    AlertDialog,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import { useDeletePromotions } from '@/lib/api/promotions'
import { useToast } from '@/components/ui/use-toast'
import { useParams } from 'react-router-dom'

interface BulkDeletePromotionsModalProps {
    promotionIds: string[]
    onClose: () => void
    onSuccess?: () => void
}

/**
 * Modal component that confirms bulk promotion deletion
 */
export const BulkDeletePromotionsModal = ({ promotionIds, onClose, onSuccess }: BulkDeletePromotionsModalProps) => {
    const { id } = useParams()
    const { t } = useTranslation('stores')
    const { t: tCommon } = useTranslation('common')
    const { toast } = useToast()
    const deletePromotions = useDeletePromotions()

    const count = promotionIds.length

    const handleDelete = async () => {
        if (promotionIds.length === 0 || !id) return

        try {
            await deletePromotions.mutateAsync({ ids: promotionIds, storeId: id })

            toast({
                title: t('promotionsTable.deleteBulkSuccess'),
                description: t('promotionsTable.deleteBulkDescription', { count }),
            })

            onSuccess?.()
            onClose()
        } catch (error) {
            toast({
                title: tCommon('error'),
                description: t('promotionsTable.deleteBulkError'),
                variant: 'destructive',
            })
        }
    }

    return (
        <AlertDialog open={true} onOpenChange={onClose}>
            <AlertDialogContent>
                <AlertDialogHeader>
                    <AlertDialogTitle>{t('promotionsTable.modals.bulkDelete.title')}</AlertDialogTitle>
                    <AlertDialogDescription>{t('promotionsTable.modals.bulkDelete.description', { count })}</AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                    <Button variant="outline" onClick={onClose} disabled={deletePromotions.isPending}>
                        {tCommon('cancel')}
                    </Button>
                    <Button variant="destructive" onClick={handleDelete} disabled={deletePromotions.isPending}>
                        {deletePromotions.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                        {tCommon('delete')}
                    </Button>
                </AlertDialogFooter>
            </AlertDialogContent>
        </AlertDialog>
    )
}

